// ============================================================
// Leave Store (Zustand)
// ============================================================

import { create } from 'zustand';
import { leaveService } from '@/services/leaveService';

const useLeaveStore = create((set, get) => ({
  // State
  leaves: [], // Legacy / general leaves state
  pendingLeaves: [],
  myLeaves: [],
  balances: [],
  leaveTypes: [],
  currentDetail: null,
  summary: null,
  loading: false,
  detailLoading: false,
  error: null,
  filters: {
    status: 'All',
    leaveType: 'All',
    search: '',
  },

  // Actions
  setFilters: (newFilters) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters },
    }));
  },

  // Fetch summary for dashboard widgets (legacy support)
  fetchSummary: async (role, teacherId, currentSchoolId) => {
    try {
      const summary = await leaveService.getLeaveSummary(role, teacherId, currentSchoolId);
      set({ summary });
      return summary;
    } catch (err) {
      console.error('Error fetching leave summary', err);
    }
  },

  // Fetch leaves (legacy support)
  fetchLeaves: async (role, teacherId, currentSchoolId) => {
    set({ loading: true, error: null });
    try {
      let data = [];
      if (role === 'teacher') {
        data = await leaveService.getMyLeaves();
      } else {
        data = await leaveService.getPendingLeaves({ schoolId: currentSchoolId });
      }
      set({ leaves: data, pendingLeaves: data, loading: false });
      return data;
    } catch (err) {
      set({ error: err.message, loading: false });
    }
  },

  // Fetch pending leave requests (for Principal & Org Admin)
  fetchPendingLeaves: async (params = {}) => {
    set({ loading: true, error: null });
    try {
      const data = await leaveService.getPendingLeaves(params);
      set({ pendingLeaves: data, leaves: data, loading: false });
    } catch (err) {
      const errMsg = err?.response?.data?.detail || err?.message || 'Failed to fetch pending leave requests';
      set({ error: errMsg, pendingLeaves: [], leaves: [], loading: false });
    }
  },

  // Fetch my leaves (for logged-in staff member)
  fetchMyLeaves: async (params = {}) => {
    set({ loading: true, error: null });
    try {
      const data = await leaveService.getMyLeaves(params);
      set({ myLeaves: data, loading: false });
    } catch (err) {
      const errMsg = err?.response?.data?.detail || err?.message || 'Failed to fetch my leave requests';
      set({ error: errMsg, myLeaves: [], loading: false });
    }
  },

  // Fetch my leave balance
  fetchBalances: async (year = new Date().getFullYear()) => {
    try {
      const balances = await leaveService.getMyBalance(year);
      set({ balances: Array.isArray(balances) ? balances : [] });
    } catch (err) {
      set({ balances: [] });
    }
  },

  // Fetch active leave types for applying leave
  fetchLeaveTypes: async (schoolId) => {
    try {
      const leaveTypes = await leaveService.getLeaveTypes(schoolId);
      set({ leaveTypes });
    } catch (err) {
      console.error('Error fetching leave types:', err);
    }
  },

  // Fetch specific leave detail & timeline
  fetchLeaveDetail: async (leaveId) => {
    set({ detailLoading: true, currentDetail: null });
    try {
      const detail = await leaveService.getMyLeaveDetail(leaveId);
      set({ currentDetail: detail, detailLoading: false });
    } catch (err) {
      console.error('Error fetching leave detail:', err);
      set({ detailLoading: false });
    }
  },

  // Apply for leave
  applyLeave: async (payload) => {
    set({ loading: true, error: null });
    try {
      const result = await leaveService.applyLeave(payload);
      // Refresh my leaves & balances
      await get().fetchMyLeaves();
      await get().fetchBalances();
      set({ loading: false });
      return result;
    } catch (err) {
      set({ error: err.message, loading: false });
      throw err;
    }
  },

  // Approve leave request
  approveLeave: async (leaveId, remarks = '') => {
    set({ loading: true, error: null });
    try {
      await leaveService.approveLeave(leaveId, remarks);
      // Remove from pending list
      set((state) => ({
        pendingLeaves: state.pendingLeaves.filter((l) => l.id !== leaveId && l.leaveRequestId !== leaveId),
        leaves: state.leaves.filter((l) => l.id !== leaveId && l.leaveRequestId !== leaveId),
        loading: false,
      }));
    } catch (err) {
      set({ error: err.message, loading: false });
      throw err;
    }
  },

  // Reject leave request
  rejectLeave: async (leaveId, remarks = '', rejectionReason = '') => {
    set({ loading: true, error: null });
    try {
      await leaveService.rejectLeave(leaveId, remarks, rejectionReason);
      // Remove from pending list
      set((state) => ({
        pendingLeaves: state.pendingLeaves.filter((l) => l.id !== leaveId && l.leaveRequestId !== leaveId),
        leaves: state.leaves.filter((l) => l.id !== leaveId && l.leaveRequestId !== leaveId),
        loading: false,
      }));
    } catch (err) {
      set({ error: err.message, loading: false });
      throw err;
    }
  },

  // Cancel my leave request
  cancelLeave: async (leaveId, remarks = '') => {
    set({ loading: true, error: null });
    try {
      await leaveService.cancelLeave(leaveId, remarks);
      // Update status in myLeaves
      set((state) => ({
        myLeaves: state.myLeaves.map((l) =>
          (l.id === leaveId || l.leaveRequestId === leaveId) ? { ...l, status: 'CANCELLED' } : l
        ),
        loading: false,
      }));
      await get().fetchBalances();
    } catch (err) {
      set({ error: err.message, loading: false });
      throw err;
    }
  },
}));

export default useLeaveStore;
